import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, functions } from "@/lib/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import PostingResultDialog from "@/components/modals/PostingResultDialog.jsx";
import { ToastContext } from "@/components/ToastProvider";
import { formatDT } from "@/utils/dates";

function StatusBadge({ s }) {
  const cls =
    s === "paid"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : s === "rejected"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : "bg-amber-50 text-amber-800 border-amber-200";
  const icon =
    s === "paid"
      ? (
          <svg className="inline mr-1" width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#34d399"/><path d="M6 10.5l3 3 5-6" stroke="#065f46" strokeWidth="2" fill="none"/></svg>
        )
      : s === "rejected"
      ? (
          <svg className="inline mr-1" width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#f87171"/><path d="M7 7l6 6M13 7l-6 6" stroke="#7f1d1d" strokeWidth="2" fill="none"/></svg>
        )
      : (
          <svg className="inline mr-1" width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#fbbf24"/><path d="M10 5v5m0 3h.01" stroke="#92400e" strokeWidth="2" fill="none"/></svg>
        );
  return <span className={`inline-block text-xs px-2 py-0.5 rounded border ${cls}`}>{icon}{s}</span>;
}

export default function PaymentDetail({ id, onClose, adminUid, resultRedirectPath = "/payments/store?status=Pending", fallbackRedirectPath = "/payments/store", disableAutoPosting = false, onPaymentVerified = null, isCoopMembershipVerification = false, memberData = null }) {
  const { notify } = useContext(ToastContext);
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [principal, setPrincipal] = useState("");
  const [interest, setInterest] = useState("");
  const [notes, setNotes] = useState("");
  const [journal, setJournal] = useState(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultVariant, setResultVariant] = useState("success");
  const [resultInfo, setResultInfo] = useState({ receiptNo: null, journalNo: null, message: "" });
  const [cashAccounts, setCashAccounts] = useState([]);
  const [depositAccountId, setDepositAccountId] = useState("");
  const [showRefund, setShowRefund] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundInfo, setRefundInfo] = useState({ ok: false, journalId: "", already: false });
  const [showVoid, setShowVoid] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voidBusy, setVoidBusy] = useState(false);
  
  // Collapsible sections state
  const [collapsedSections, setCollapsedSections] = useState({
    amount: false,
    audit: false,
    info: false,
    proof: false,
    loan: false,
    status: false
  });
  
  const navigate = useNavigate();
  const postUnsubRef = useRef(null);
  const postTimerRef = useRef(null);

  const pad5 = (v) => (v != null ? String(v).padStart(5, "0") : "");

  // Toggle section collapse state
  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Collapsible section component
  const CollapsibleSection = ({ id, title, children, defaultCollapsed = false }) => {
    const isCollapsed = collapsedSections[id] ?? defaultCollapsed;
    
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
        <button
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between text-sm font-semibold text-gray-900 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 p-5 transition-all duration-200"
        >
          <span className="text-base">{title}</span>
          <svg
            className={`w-5 h-5 transition-transform text-gray-500 ${isCollapsed ? 'rotate-90' : '-rotate-90'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <div className={`transition-all duration-300 overflow-hidden ${
          isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'}`}>
          <div className="p-5 pt-0">
            {children}
          </div>
        </div>
      </div>
    );
  };

  // Lock background scroll while modal is open
  useEffect(() => {
    // Remove the problematic body scroll lock that was causing freezing
    // The modal will handle its own scrolling properly
    return () => {
      // Cleanup function - no need to restore body overflow
    };
  }, []);

  function cleanupPostingWatch() {
    if (postUnsubRef.current) {
      try { postUnsubRef.current(); } catch {}
      postUnsubRef.current = null;
    }
    if (postTimerRef.current) {
      clearTimeout(postTimerRef.current);
      postTimerRef.current = null;
    }
  }

  useEffect(() => () => cleanupPostingWatch(), []);

  useEffect(() => {
    if (!id) return; // Guard against undefined id
    const ref = doc(db, "payments", id);
    const unsub = onSnapshot(ref, (s) => {
      const d = s.exists() ? { id: s.id, ...s.data() } : null;
      setP(d);
      if (d) {
        setNotes(d.notes || "");
        if (d.type === "loan_repayment") {
          setPrincipal(String(d.principalPortion ?? d.amount ?? ""));
          setInterest(String(d.interestPortion ?? 0));
        }
        setDepositAccountId(d.depositAccountId || "");
      }
    });
    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const qj = query(collection(db, "journalEntries"), where("linkedPaymentId", "==", id), limit(1));
    const unsub = onSnapshot(qj, (s) => {
      setJournal(s.empty ? null : { id: s.docs[0].id, ...s.docs[0].data() });
    });
    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setAuditLoading(true);
    const ql = query(collection(db, "adminLogs"), where("paymentId", "==", id), orderBy("ts", "desc"), limit(20));
    const unsub = onSnapshot(ql, (snap) => {
      setAuditLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setAuditLoading(false);
    }, () => {
      setAuditLogs([]);
      setAuditLoading(false);
    });
    return () => unsub();
  }, [id]);

  useEffect(() => {
    const fn = async () => {
      try {
        const snap = await getDocs(collection(db, "accounts"));
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const candidates = rows.filter((a) => {
          const t = String(a.type || "").toLowerCase();
          const n = `${a.main} ${a.individual || ""}`.toLowerCase();
          return t === "asset" || n.includes("cash") || n.includes("bank") || n.includes("gcash");
        });
        setCashAccounts(candidates);
      } catch {}
    };
    fn();
  }, []);

  // Don't render if no ID is provided
  if (!id) return null;
  
  const journalRefNumber = (journal && (journal.refNumber || (journal.journalNo != null ? pad5(journal.journalNo) : ""))) || "";
  const receiptNo = p?.receiptNo || "";
  if (!p) return null;
  const amount = Number(p.amount || 0);
  const isLoan = p.type === "loan_repayment";
  
  // Check if payment is already posted to accounting
  const isAlreadyPosted = p.status === "paid" && (p.receiptNo || p.journalNo);
  
  // For cooperative membership verification, check member's posted status
  let isCoopPaymentAlreadyPosted = false;
  if (isCoopMembershipVerification && memberData && p) {
    if (p.type === "entrance_fee") {
      isCoopPaymentAlreadyPosted = memberData.entranceFeePosted === true;
    } else if (p.type === "share_capital") {
      isCoopPaymentAlreadyPosted = memberData.shareCapitalPosted === true;
    }
  }
  
  const canAct = (p.status === "pending" || (p.status === "paid" && !isAlreadyPosted)) && !isCoopPaymentAlreadyPosted && !busy;
  

  
  // Button should be disabled if:
  // 1. Payment is already posted to accounting (has receiptNo or journalNo)
  // 2. Payment is not in a state where it can be acted upon
  // 3. System is busy
  
  const canRefund = p.status === "paid" && !refundBusy;
  const canVoid = p.status === "pending" && !voidBusy;

  const pNum = Number(principal || 0);
  const iNum = Number(interest || 0);
  const sumOk = !isLoan || Math.abs(pNum + iNum - amount) < 0.005;

  function navigateIfNeeded(path) {
    if (path === null) return; // explicitly disabled
    if (typeof path === "string" && path.length) navigate(path);
  }

  // Automatically update member verification status when payments are posted
  const updateMemberVerificationStatus = async (memberUid, paymentType, isPosted) => {
    try {
      const updates = {};
      
      if (paymentType === 'entrance_fee') {
        updates.entranceFeePosted = isPosted;
        updates.entranceFeeVerified = isPosted;
      } else if (paymentType === 'share_capital') {
        updates.shareCapitalPosted = isPosted;
        updates.shareCapitalVerified = isPosted;
      }

      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, 'members', memberUid), updates);
      }
    } catch (error) {
              // Error updating member verification status
    }
  };

  function watchForPosting(paymentId) {
    cleanupPostingWatch();
    const pref = doc(db, "payments", paymentId);

    // safety timeout: optimistic success and optional redirect
    postTimerRef.current = setTimeout(() => {
      setIsApproving(false);
      setResultVariant("success");
      setResultInfo({ receiptNo: null, journalNo: null, message: "" });
      setResultOpen(true);
      // Do not auto-redirect here; let dialog onClose handle it via props
    }, 15000);

    postUnsubRef.current = onSnapshot(
      pref,
      (snap) => {
        const v = snap.data();
        if (!v) return;
        if (v.postingError) {
          cleanupPostingWatch();
          setIsApproving(false);
          setResultVariant("error");
          const msg = v.postingError?.message || String(v.postingError);
          setResultInfo({ receiptNo: null, journalNo: null, message: msg });
          setResultOpen(true);
          return;
        }
        if (v.status === "paid" && (v.receiptNo || v.journalNo)) {
          cleanupPostingWatch();
          setIsApproving(false);
          setResultVariant("success");
          setResultInfo({ receiptNo: v.receiptNo || null, journalNo: v.journalNo || null, message: "" });
          setResultOpen(true);
          
          // Automatically update member verification status for cooperative membership
          if (isCoopMembershipVerification && memberData && memberData.uid) {
            updateMemberVerificationStatus(memberData.uid, p.type, true);
          }
        }
      },
      () => {
        cleanupPostingWatch();
        setIsApproving(false);
        setResultVariant("error");
        setResultInfo({ receiptNo: null, journalNo: null, message: "Listener error" });
        setResultOpen(true);
      }
    );
  }

  async function approve() {
    setErr("");
    setSuccess("");
    if (!sumOk) {
      setErr("Principal + Interest must equal Amount.");
      return;
    }
    if (["gcash_manual", "bank_transfer", "static_qr"].includes(p.method || "") && !p.proofURL) {
      setErr("Proof file is required for manual methods.");
      return;
    }
    setBusy(true);
    setIsApproving(true);
    try {
      const ref = doc(db, "payments", id);
      await updateDoc(ref, {
        confirmedBy: adminUid || null,
        confirmedAt: serverTimestamp(),
        notes: (notes || "").trim(),
        ...(depositAccountId ? { depositAccountId } : {}),
        ...(isLoan ? { principalPortion: Number(Number(pNum).toFixed(2)), interestPortion: Number(Number(iNum).toFixed(2)) } : {}),
      });
      await updateDoc(ref, { status: "paid" });
      
      // Only auto-post to accounting if not disabled
      if (!disableAutoPosting) {
        watchForPosting(id);
        setSuccess("Posting…");
      } else {
        // For membership verification, just mark as verified without auto-posting
        setIsApproving(false);
        setSuccess("Payment verified successfully. No auto-posting to accounting.");
        onPaymentVerified?.(); // Notify parent if a callback is provided
      }
      
      await addDoc(collection(db, "adminLogs"), {
        ts: serverTimestamp(),
        actorUid: adminUid || null,
        action: "PAYMENT_APPROVAL",
        paymentId: id,
        paymentType: p.type || null,
        amount: amount,
        memberUid: p.userId || p.uid || null,
        ...(isLoan ? { principalPortion: pNum, interestPortion: iNum } : {}),
      });
    } catch (e) {
      setErr(e?.message || String(e));
      setIsApproving(false);
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    const reason = window.prompt("Reason for rejection (required):");
    if (!reason || !reason.trim()) return;
    setBusy(true);
    setErr("");
    setSuccess("");
    try {
      const ref = doc(db, "payments", id);
      await updateDoc(ref, {
        status: "rejected",
        confirmedBy: adminUid || null,
        confirmedAt: serverTimestamp(),
        notes: `${(notes || "").trim()}${notes ? " | " : ""}Rejected: ${reason.trim()}`,
      });
      await addDoc(collection(db, "adminLogs"), {
        ts: serverTimestamp(),
        actorUid: adminUid || null,
        action: "PAYMENT_REJECTION",
        paymentId: id,
        paymentType: p.type || null,
        amount: amount,
        memberUid: p.userId || p.uid || null,
        reason: reason.trim(),
      });
      setSuccess("Payment rejected.");
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 touch-none overflow-y-auto">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] border border-gray-100 w-full max-w-2xl min-h-fit max-h-[90vh] overflow-hidden touch-auto transform scale-100 animate-in fade-in-0 zoom-in-95 duration-300">
        {/* Header - Always visible */}
        <div className="sticky top-0 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-6 py-5 rounded-t-2xl z-20 flex-shrink-0 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-2xl font-bold text-gray-900 break-words">{p.memberName || p.userEmail || p.userId || p.uid || "Member"}</div>
              <div className="text-sm text-gray-600 mt-2 break-words">
                <span className="font-semibold text-gray-800">{p.type || "—"}</span>
                {p.method && (
                  <>
                    <span className="mx-2 text-gray-400">•</span>
                    <span className="text-gray-600">{p.method === "static_qr" ? "QR (manual)" : p.method}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <StatusBadge s={p.status || "pending"} />
              <button 
                className="rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-all duration-200 hover:scale-105" 
                onClick={onClose} 
                aria-label="Close"
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-100px)]">

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Amount & Receipt / Journal */}
          <CollapsibleSection id="amount" title="Payment Details" defaultCollapsed={false}>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-100 shadow-sm">
              <div className="text-sm text-blue-600 mb-2 font-medium">Payment Amount</div>
              <div className="text-4xl font-bold text-blue-900">₱{Number(p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="mt-2 text-xs text-blue-500">Verified payment amount</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-600 mb-1">Reference No</div>
                <div className="font-medium break-all">{journalRefNumber || "—"}</div>
              </div>
              {receiptNo && (
                <div>
                  <div className="text-gray-600 mb-1">Receipt No</div>
                  <div className="font-medium">{receiptNo}</div>
                </div>
              )}
              {journalRefNumber && (
                <div>
                  <div className="text-gray-600 mb-1">Journal No</div>
                  <div className="font-medium">{journalRefNumber}</div>
                </div>
              )}
            </div>
            <div className="mt-2 text-xs text-ink/60">Posting: {(p.posting?.status || "—")} • Attempts: {p.posting?.attempts || 0}</div>
            {p.postingError && (
              <div className="mt-2 rounded border border-rose-200 bg-rose-50 text-rose-800 p-2 text-sm">
                <div className="break-words">Posting error: {p.postingError?.message || String(p.postingError)}</div>
                <div className="mt-2 flex flex-col sm:flex-row gap-2 items-start">
                  <button className="btn btn-sm btn-outline" onClick={() => navigator.clipboard?.writeText(p.postingError?.message || String(p.postingError))}>Copy error</button>
                  <a className="btn btn-sm btn-outline" href="/admin/settings/accounting" target="_self">Open Accounting Settings</a>
                  <button className="btn btn-sm btn-primary" onClick={async () => { setRetryBusy(true); try { const call = httpsCallable(functions, "repostPayment"); await call({ paymentId: p.id }); } catch (e) { notify(e?.message || String(e), { type: "error" }); } finally { setRetryBusy(false); } }} disabled={retryBusy}>
                    {retryBusy ? "Retrying…" : "Retry Post"}
                  </button>
                </div>
              </div>
            )}
            {p.posting?.status === "failed" && (
              <div className="mt-2 rounded border border-rose-200 bg-rose-50 text-rose-800 p-2 text-sm">
                <div className="break-words">Posting error: {p.posting?.error?.message || p.posting?.error || "Unknown"}</div>
                <div className="mt-2 flex flex-col sm:flex-row gap-2 items-start">
                  <button className="btn btn-sm btn-outline" onClick={() => navigator.clipboard?.writeText(p.posting?.error?.message || p.posting?.error || "")}>Copy error</button>
                  <a className="btn btn-sm btn-outline" href="/admin/settings/accounting" target="_self">Open Accounting Settings</a>
                  <button className="btn btn-sm btn-primary" onClick={async () => { setRetryBusy(true); try { const call = httpsCallable(functions, "repostPayment"); await call({ paymentId: p.id }); } catch (e) { notify(e?.message || String(e), { type: "error" }); } finally { setRetryBusy(false); } }} disabled={retryBusy}>
                    {retryBusy ? "Retrying…" : "Retry Post"}
                  </button>
                </div>
              </div>
            )}
            {p.status === "paid" && p.receiptNo && (
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <a className="btn btn-sm btn-outline" href={`/receipt/${p.id}`} target="_self">View Receipt</a>
                <button className="btn btn-sm btn-outline" onClick={async () => { try { await navigator.clipboard?.writeText(`${location.origin}/receipt/${p.id}`); notify("Receipt link copied.", { type: "success", duration: 2500 }); } catch {} }}>Copy receipt link</button>
              </div>
            )}
          </CollapsibleSection>

          {/* Audit Trail */}
          <CollapsibleSection id="audit" title="Audit Trail" defaultCollapsed={true}>
            {auditLoading ? (
              <div className="text-xs text-ink/60">Loading…</div>
            ) : auditLogs.length === 0 ? (
              <div className="text-xs text-ink/60">No admin actions found.</div>
            ) : (
              <ul className="space-y-1">
                {auditLogs.map((log) => (
                  <li key={log.id} className="text-xs flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="text-ink/60 shrink-0">{log.ts?.toDate ? formatDT(log.ts.toDate()) : "—"}</span>
                    <span className="font-mono bg-gray-100 rounded px-1 shrink-0">{log.action}</span>
                    <span className="text-ink/80 break-all">{log.actorUid || "?"}</span>
                    {log.reason && <span className="text-ink/60 break-words">{log.reason}</span>}
                    {log.amount != null && <span className="text-ink/60 shrink-0">₱{Number(log.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleSection>

          {/* Info */}
          <CollapsibleSection id="info" title="Payment Information" defaultCollapsed={true}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-ink/60">Reference No</div>
                <div className="break-all">{p.referenceNo || p.refNo || "—"}</div>
              </div>
              {p.method === "cod" && (
                <div>
                  <div className="text-ink/60">Collected</div>
                  <div className="break-words">
                    {p.collectedBy ? (
                      <>
                        by <span className="font-mono">{p.collectedBy}</span>
                        {p.collectedAt && (
                          <span className="text-ink/60"> • {formatDT(p.collectedAt)}</span>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              )}
              {p.method === "gcash_manual" && (
                <>
                  <div>
                    <div className="text-ink/60">GCash Number</div>
                    <div className="font-mono break-all">{p.gcashNumber || "—"}</div>
                  </div>
                  <div>
                    <div className="text-ink/60">GCash Name</div>
                    <div className="break-words">{p.gcashName || "—"}</div>
                  </div>
                </>
              )}
              {p.method === "bank_transfer" && (
                <>
                  <div>
                    <div className="text-ink/60">Bank</div>
                    <div className="break-words">{p.bankName || "—"}</div>
                  </div>
                  <div>
                    <div className="text-ink/60">Account</div>
                    <div className="font-mono break-all">{p.bankAccount || "—"}</div>
                  </div>
                </>
              )}
              {p.method === "static_qr" && (
                <div className="sm:col-span-2">
                  <div className="text-ink/60">QR Code</div>
                  <div className="break-all">{p.qrCode || "—"}</div>
                </div>
              )}
              {p.notes && (
                <div className="sm:col-span-2">
                  <div className="text-ink/60">Notes</div>
                  <div className="break-words">{p.notes}</div>
                </div>
              )}
              {p.loanId && (
                <div className="sm:col-span-2">
                  <div className="text-ink/60">Loan</div>
                  <div className="break-all">{p.loanId}</div>
                </div>
              )}
              {p.loanId && (
                <div className="sm:col-span-2">
                  <div className="text-ink/60">Created</div>
                  <div>{formatDT(p.createdAt)}</div>
                </div>
              )}
              <div className="sm:col-span-2">
                <div className="text-ink/60">Linked</div>
                <div>
                  {p.linkedId ? (
                    p.type === "purchase" ? (
                      <a className="underline break-all" href={`/orders/${p.linkedId}`}>View Order</a>
                    ) : p.type === "loan_repayment" ? (
                      <a className="underline break-all" href={`/loans/${p.linkedId}`}>View Loan</a>
                    ) : (
                      <span className="break-all">{p.linkedId}</span>
                    )
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-ink/60">Notes</div>
                <textarea className="w-full border rounded px-2 py-1" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <div className="text-ink/60">Deposit to (optional)</div>
                <select className="input w-full" value={depositAccountId} onChange={(e) => setDepositAccountId(e.target.value)}>
                  <option value="">— Default via settings —</option>
                  {cashAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.code ? `${a.code} • ` : "") + a.main + (a.individual ? " / " + a.individual : "")}
                    </option>
                  ))}
                </select>
                <div className="text-xs text-ink/60 mt-1">Overrides the cash account for this payment only.</div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Proof viewer */}
          <CollapsibleSection id="proof" title="Proof of Payment" defaultCollapsed={true}>
            {p.proofURL ? (
              <div className="space-y-2">
                {String(p.proofURL).match(/\.(png|jpe?g|webp|gif)$/i) ? (
                  <img src={p.proofURL} alt="Proof" className="max-h-[60vh] w-auto rounded border" />
                ) : String(p.proofURL).match(/\.(pdf)$/i) ? (
                  <iframe title="Proof" src={p.proofURL} className="w-full h-[60vh] border rounded" />
                ) : (
                  <a className="underline break-all" href={p.proofURL} target="_blank" rel="noreferrer">Open proof</a>
                )}
                <div>
                  <a className="btn btn-sm btn-outline" href={p.proofURL} target="_blank" rel="noreferrer">Open in new tab</a>
                </div>
              </div>
            ) : (
              <div className="text-sm text-ink/60">No proof uploaded.</div>
            )}
          </CollapsibleSection>

          {/* Loan split */}
          {isLoan && (
            <CollapsibleSection id="loan" title="Loan Split" defaultCollapsed={true}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-ink/60">Principal Portion</span>
                  <input className="input w-full" type="number" step="0.01" value={principal} onChange={(e) => setPrincipal(e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-xs text-ink/60">Interest Portion</span>
                  <input className="input w-full" type="number" step="0.01" value={interest} onChange={(e) => setInterest(e.target.value)} />
                </label>
              </div>
              <div className={`mt-2 text-sm ${sumOk ? "text-emerald-700" : "text-rose-700"}`}>
                {sumOk ? "OK" : `Must equal total amount ₱${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </div>
              <div className="text-xs text-ink/60 mt-1">Interest → credited to Interest Income.</div>
            </CollapsibleSection>
          )}

          {/* Status banners */}
          <CollapsibleSection id="status" title="Status Information" defaultCollapsed={true}>
            {(p.status === "refunded" || p.refundJournalId) && (
              <div className="rounded border border-amber-200 bg-amber-50 text-amber-800 p-2 text-sm">
                <div className="break-words">
                  Refunded{p.refundJournalId ? (
                    <> • Journal: <a className="underline break-all" href={`/admin/journals/${p.refundJournalId}`}>{p.refundJournalId}</a></>
                  ) : null}
                  {p.refundReason ? <> • Reason: {p.refundReason}</> : null}
                </div>
              </div>
            )}
            {p.status === "voided" && (
              <div className="rounded border border-gray-200 bg-gray-50 text-gray-800 p-2 text-sm">
                <div className="break-words">
                  Voided{p.voidReason ? <> • Reason: {p.voidReason}</> : null}
                </div>
              </div>
            )}
          </CollapsibleSection>
          
          {/* Actions */}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button className="btn btn-primary flex-1 sm:flex-none justify-center" disabled={!canAct || (isLoan && !sumOk) || isApproving} onClick={approve}>
              {isApproving ? "Posting…" : "Approve & Post"}
            </button>
            <button className="btn btn-outline flex-1 sm:flex-none justify-center" disabled={!canAct} onClick={reject}>Reject</button>
            {p.status === "paid" && (
              <button className="btn btn-outline btn-amber flex-1 sm:flex-none justify-center" disabled={!canRefund} onClick={() => setShowRefund(true)}>
                Refund
              </button>
            )}
            {p.status === "pending" && (
              <button className="btn btn-outline flex-1 sm:flex-none justify-center" disabled={!canVoid} onClick={() => setShowVoid(true)}>
                Void
              </button>
            )}
            {success && <span className="text-sm text-emerald-700 sm:ml-2 break-words">{success}</span>}
            {err && <span className="text-sm text-rose-700 sm:ml-2 break-words">{err}</span>}
          </div>
        </div>
        </div>
      </div>

      <PostingResultDialog
        open={resultOpen}
        variant={resultVariant}
        receiptNo={resultInfo.receiptNo}
        journalNo={resultInfo.journalNo}
        message={resultInfo.message}
        paymentId={id}
        onClose={() => {
          setResultOpen(false);
          // For modal usage, just close; navigation is managed by parent if needed
          // Keep compatibility with explicit redirect paths if parent passes them
          // No-op here
        }}
      />

      {/* Refund Modal */}
      {showRefund && (
        <div className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/40" onClick={() => !refundBusy && setShowRefund(false)} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl w-[480px] max-w-[95vw] p-4">
            <div className="text-lg font-semibold mb-2">Refund Payment</div>
            <div className="text-sm text-ink/60 mb-2">Provide a reason. This posts a reversing journal.</div>
            <label className="block mb-3">
              <div className="text-xs text-ink/60">Reason</div>
              <input className="input w-full" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} maxLength={200} />
            </label>
            <div className="flex justify-end gap-2">
              <button className="btn btn-outline" disabled={refundBusy} onClick={() => setShowRefund(false)}>Cancel</button>
              <button className="btn btn-amber" disabled={!refundReason.trim() || refundBusy} onClick={async () => { setRefundBusy(true); try { const call = httpsCallable(functions, "refundPayment"); const { data } = await call({ paymentId: id, reason: refundReason.trim() }); const r = data || {}; setRefundInfo({ ok: !!r.ok, journalId: r.refundJournalId || "", already: !!r.already }); setShowRefund(false); } catch (e) { notify(e?.message || String(e), { type: "error" }); } finally { setRefundBusy(false); } }}>
                {refundBusy ? "Refunding…" : "Confirm Refund"}
              </button>
            </div>
          </div>
        </div>
      )}

    {/* Void Modal */}
      {showVoid && (
        <div className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/40" onClick={() => !voidBusy && setShowVoid(false)} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl w-[480px] max-w-[95vw] p-4">
            <div className="text-lg font-semibold mb-2">Void Payment</div>
            <div className="text-sm text-ink/60 mb-2">Provide a reason. No journals will be created.</div>
            <label className="block mb-3">
              <div className="text-xs text-ink/60">Reason</div>
              <input className="input w-full" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} maxLength={200} />
            </label>
            <div className="flex justify-end gap-2">
              <button className="btn btn-outline" disabled={voidBusy} onClick={() => setShowVoid(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!voidReason.trim() || voidBusy} onClick={async () => { setBusy(true); try { const call = httpsCallable(functions, "voidPayment"); await call({ paymentId: id, reason: voidReason.trim() }); setShowVoid(false); } catch (e) { notify(e?.message || String(e), { type: "error" }); } finally { setBusy(false); } }}>
                {voidBusy ? "Voiding…" : "Confirm Void"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
